import { Receipt, Wallet } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { InfoTooltip } from "@/lib/dashboard/InfoTooltip";
import { useI18n } from "@/hooks/useI18n";
import { useMoneyCopy } from "@/lib/i18n/money-copy";

interface PaymentStatusCardProps {
  paidAmount: number;
  pendingAmount: number;
  /** Quota di `pendingAmount` gia' scaduta: aggiunge una riga e tinge di rosso. */
  overdueAmount: number;
  /** Pagato + in attesa. Viene dai piani rate, non dal venduto. */
  totalAmount: number;
  /**
   * Commissioni Place2Be incassate fuori da Stripe e mai versate.
   *
   * Non e' una quota di `pendingAmount`: quella e' cassa che deve ancora arrivare dal
   * cliente, questa e' denaro gia' incassato da cui la fee non e' passata. Sta nella
   * stessa card perche' e' cassa, ma sotto una riga di separazione e fuori dalla barra,
   * che rappresenta un'altra somma.
   */
  platformFeeDueAmount?: number | null;
  platformFeeDueCount?: number | null;
  /**
   * true per l'admin (`meta.scope === 'platform'`): lo stesso numero e' un credito verso
   * le agenzie invece che un debito proprio, e cambiare solo l'etichetta e' piu' onesto
   * che tenerne due versioni.
   */
  isPlatformScope?: boolean;
  formatCurrency: (value: number) => string;
}

/**
 * Lo stato dei pagamenti dell'intero perimetro, in una card sola accanto ai KPI.
 *
 * Erano tre riquadri in fila -- pagato, in attesa, totale -- e occupavano mezza griglia
 * per dire una cosa sola, costringendo a leggerli tutti e tre per capire a che punto si
 * e'. Qui il totale e' il numero grande, la barra risponde a "quanto manca" senza fare
 * sottrazioni, e i due addendi restano sotto per chi vuole le cifre.
 *
 * Sta a destra e alta quanto la colonna dei KPI: e' l'unica card che parla di cassa, e
 * separarla dalle altre e' cio' che la rende trovabile.
 */
export function PaymentStatusCard({
  paidAmount,
  pendingAmount,
  overdueAmount,
  totalAmount,
  platformFeeDueAmount,
  platformFeeDueCount,
  isPlatformScope = false,
  formatCurrency,
}: PaymentStatusCardProps) {
  const { t } = useI18n();
  const money = useMoneyCopy();

  const hasTotal = Number.isFinite(totalAmount) && totalAmount > 0;
  // Clamp: un saldo in eccesso non deve far sfondare la barra oltre il 100%.
  const paidPercent = hasTotal
    ? Math.min(100, Math.max(0, (paidAmount / totalAmount) * 100))
    : 0;
  const hasOverdue = overdueAmount > 0;
  const hasFeeDue = (platformFeeDueAmount ?? 0) > 0;

  return (
    <Card className="h-full">
      <CardContent className="p-4 h-full flex flex-col gap-3">
        <div className="flex items-center gap-2">
          <div className="flex items-center justify-center h-8 w-8 rounded-lg flex-shrink-0 bg-blue-50 text-blue-600">
            <Wallet className="h-4 w-4" />
          </div>
          <span className="text-sm text-gray-500">
            {t("dashboard.overview.paymentStatus.column")}
            {" "}
            <InfoTooltip text={t("dashboard.overview.paymentStatus.infoOverview")} />
          </span>
        </div>

        {/*
          Senza totale la card non stampa "0": `formatCurrency` traduce un campo assente
          in un euro zero perfettamente credibile, e uno zero inventato e' peggio di un
          buco dichiarato. Il trattino copre due casi che a schermo si equivalgono --
          nessun piano di pagamento, oppure una RPC che non manda ancora questi campi.
        */}
        {!hasTotal ? (
          <div className="flex flex-col gap-1">
            <span className="text-2xl font-semibold text-gray-400">—</span>
            <span className="text-xs text-gray-500">
              {t("dashboard.overview.paymentStatus.empty")}
            </span>
          </div>
        ) : (
          <>
          <div>
            <div className="text-2xl font-semibold text-gray-900 tabular-nums">
              {formatCurrency(totalAmount)}
            </div>
            <span className="text-xs text-gray-500">
              {t(`${money}.total`)}
            </span>
          </div>

          <div
            className="h-2 w-full rounded-full bg-gray-100 overflow-hidden"
            role="img"
            aria-label={t("dashboard.overview.paymentStatus.barLabel", {
              paid: formatCurrency(paidAmount),
              total: formatCurrency(totalAmount),
            })}
          >
            <div
              className="h-full rounded-full bg-emerald-500"
              style={{ width: `${paidPercent}%` }}
            />
          </div>

          <div className="flex flex-col gap-1.5 mt-auto text-sm">
            <div className="flex items-center justify-between gap-2">
              <span className="inline-flex items-center gap-1.5 text-gray-600">
                <span className="h-2 w-2 rounded-full bg-emerald-500 flex-shrink-0" />
                {t(`${money}.paid`)}
              </span>
              <span className="font-medium text-emerald-700 tabular-nums">
                {formatCurrency(paidAmount)}
              </span>
            </div>

            <div className="flex items-center justify-between gap-2">
              <span className="inline-flex items-center gap-1.5 text-gray-600">
                <span
                  className={`h-2 w-2 rounded-full flex-shrink-0 ${
                    hasOverdue ? "bg-red-500" : "bg-amber-400"
                  }`}
                />
                {t(`${money}.pending`)}
              </span>
              <span
                className={`font-medium tabular-nums ${
                  hasOverdue ? "text-red-600" : "text-amber-700"
                }`}
              >
                {formatCurrency(pendingAmount)}
              </span>
            </div>

            {/* Lo scaduto e' una quota dell'in attesa, non un terzo addendo: sta sotto,
                piccolo, senza entrare nella somma che la barra rappresenta. */}
            {hasOverdue && (
              <span className="text-xs text-red-600">
                {t("dashboard.overview.paymentStatus.overdueOf", {
                  amount: formatCurrency(overdueAmount),
                })}
              </span>
            )}
          </div>
          </>
        )}

        {/* Fuori dal ramo `hasTotal`: un debito aperto esiste anche su un perimetro senza
            piani rate attivi, e nasconderlo insieme alla barra lo farebbe sparire proprio
            quando e' l'unica cosa rimasta da guardare. */}
        {hasFeeDue && (
          <div className="mt-auto border-t border-amber-200 pt-2 flex items-start justify-between gap-2">
            <span className="inline-flex items-center gap-1.5 text-sm text-amber-800">
              <Receipt className="h-3.5 w-3.5 flex-shrink-0" />
              {t(
                isPlatformScope
                  ? "dashboard.overview.paymentStatus.feeToRecover"
                  : "dashboard.overview.paymentStatus.feeToPay"
              )}
              {" "}
              <InfoTooltip
                text={t(
                  isPlatformScope
                    ? "dashboard.overview.paymentStatus.feeToRecoverInfo"
                    : "dashboard.overview.paymentStatus.feeToPayInfo"
                )}
              />
            </span>
            <span className="flex flex-col items-end leading-tight">
              <span className="font-semibold text-amber-800 tabular-nums">
                {formatCurrency(platformFeeDueAmount ?? 0)}
              </span>
              {(platformFeeDueCount ?? 0) > 0 && (
                <span className="text-xs text-amber-700">
                  {t("dashboard.overview.paymentStatus.feeCount", {
                    count: platformFeeDueCount ?? 0,
                  })}
                </span>
              )}
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default PaymentStatusCard;
